import type { AdminTicket } from "@sd/types/admin";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { useAuth } from "@sd/hooks/useAuth";
import { isAgentShellRole, isExternalSdRole } from "@sd/lib/sdRoles";
import {
  formatStatusLabel,
  statusColors,
} from "@sd/constants/ticketStatuses";
import { formatTicketTypeLabel } from "@sd/types/ticket";
import { formatTimeAgo } from "@sd/lib/ticketDetailActivity";
import {
  cleanTicketDescription,
  formatDetailDate,
  isEmptyDisplayValue,
} from "@sd/lib/ticketDetailDisplay";
import { formatSlaStatusLabel } from "@sd/lib/ticketStreamUtils";
import { TicketDetailFilesSection } from "@sd/components/tickets/TicketDetailFilesSection";
import { TicketDetailAddNoteSection } from "@sd/components/tickets/TicketDetailAddNoteSection";
import { TicketDetailActivitySection } from "@sd/components/tickets/TicketDetailActivitySection";
import { TicketDescriptionContent } from "@sd/components/common/TicketDescriptionContent";
import type { TicketNoteEntry } from "@sd/lib/ticketNotesApi";
import type { TicketActivityEntry } from "@sd/lib/ticketDetailActivity";
import type { Attachment } from "@sd/types/ticket";

type TicketDetailDisplay = {
  requesterName: string;
  assigneeName: string;
  priorityLabel: string;
  createdByName: string;
};

function AttributeRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-0.5 text-sm sm:flex-row sm:gap-3">
      <div className="shrink-0 font-semibold text-foreground/85 sm:w-28 sm:text-right">
        {label}:
      </div>
      <div className="min-w-0 flex-1 break-words text-foreground">{children}</div>
    </div>
  );
}

export function TicketDetailIssueView({
  ticket,
  display,
  activityEntries,
  activityPending,
  activityError = false,
  notes,
  notesPending,
  notesError = false,
  attachments,
  attachmentsLoading,
  resolveUserLabel,
  showAddNoteSection = true,
}: {
  ticket: AdminTicket;
  display: TicketDetailDisplay;
  activityEntries: TicketActivityEntry[];
  activityPending: boolean;
  activityError?: boolean;
  notes: TicketNoteEntry[];
  notesPending: boolean;
  notesError?: boolean;
  attachments: Attachment[];
  attachmentsLoading: boolean;
  resolveUserLabel: (raw: string | undefined | null) => string;
  /** Admin full detail: show inline add-note box. Agent/Super Agent use workbench popup instead. */
  showAddNoteSection?: boolean;
}) {
  const { role } = useAuth();
  const showChangeRequest = !isAgentShellRole(role) && ticket.changeRequest;
  const hideAssigneeRequester = isExternalSdRole(role);
  const typeLabel = ticket.hasTicketTypeFromApi
    ? formatTicketTypeLabel(ticket.ticketType, ticket.ticketTypeRaw)
    : null;
  const ticketRef = ticket.uiId ?? ticket.id;
  const description = cleanTicketDescription(ticket.description);
  const createdAgo = formatTimeAgo(ticket.createdAt);
  const updatedAgo = formatTimeAgo(ticket.updatedAt);

  const leftAttributes: { label: string; value: React.ReactNode }[] = [];
  const rightAttributes: { label: string; value: React.ReactNode }[] = [];

  leftAttributes.push({
    label: "Status",
    value: formatStatusLabel(ticket.status),
  });

  if (!isEmptyDisplayValue(ticket.projectName)) {
    leftAttributes.push({ label: "Project", value: ticket.projectName });
  }

  if (!isEmptyDisplayValue(display.priorityLabel)) {
    leftAttributes.push({ label: "Priority", value: display.priorityLabel });
  }

  if (!hideAssigneeRequester) {
    leftAttributes.push({
      label: "Assignee",
      value:
        !isEmptyDisplayValue(display.assigneeName) &&
        display.assigneeName !== "Unassigned" ? (
          <span className="text-primary">{display.assigneeName}</span>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    });
  }

  const startDate = formatDetailDate(ticket.createdAt);
  if (startDate) {
    rightAttributes.push({ label: "Start date", value: startDate });
  }

  const dueDate = formatDetailDate(ticket.deadline);
  if (dueDate) {
    rightAttributes.push({ label: "Due date", value: dueDate });
  }

  const closedDate = formatDetailDate(ticket.resolvedAt);
  if (closedDate) {
    rightAttributes.push({ label: "Closed on", value: closedDate });
  }

  if (!hideAssigneeRequester && !isEmptyDisplayValue(display.requesterName)) {
    leftAttributes.push({ label: "Requester", value: display.requesterName });
  }

  if (!isEmptyDisplayValue(ticket.department)) {
    leftAttributes.push({ label: "Department", value: ticket.department });
  }

  if (!isEmptyDisplayValue(ticket.category)) {
    leftAttributes.push({ label: "Category", value: ticket.category });
  }

  if (!isEmptyDisplayValue(ticket.channel)) {
    rightAttributes.push({ label: "Channel", value: ticket.channel });
  }

  if (ticket.hasSlaFromApi) {
    rightAttributes.push({
      label: "SLA",
      value: formatSlaStatusLabel(ticket.slaStatus),
    });
  }

  if (showChangeRequest) {
    const cr = ticket.changeRequest!;
    if (cr.crNumber) {
      leftAttributes.push({ label: "Change request", value: cr.crNumber });
    }
    const requested = formatDetailDate(cr.requestedDate);
    if (requested) {
      rightAttributes.push({ label: "Requested date", value: requested });
    }
    const expected = formatDetailDate(cr.expectedCompletion);
    if (expected) {
      rightAttributes.push({ label: "Expected completion", value: expected });
    }
    if (cr.assignedEmployeeIds && cr.assignedEmployeeIds.length > 0) {
      leftAttributes.push({
        label: "CR employees",
        value: cr.assignedEmployeeIds
          .map(id => resolveUserLabel(id))
          .join(", "),
      });
    }
    if (cr.status) {
      rightAttributes.push({ label: "CR status", value: cr.status });
    }
  }

  return (
    <div className="min-w-0 w-full p-4 sm:p-6">
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <h2 className="text-lg font-normal text-foreground">
          {typeLabel ? `${typeLabel} ` : ""}
          <span className="font-semibold">#{ticketRef}</span>
        </h2>
        <Badge
          variant="outline"
          className={cn(
            "border-transparent text-xs uppercase tracking-wide",
            statusColors[ticket.status]
          )}
        >
          {formatStatusLabel(ticket.status)}
        </Badge>
        {showChangeRequest && ticket.changeRequest?.crNumber ? (
          <Badge
            variant="secondary"
            className="border-transparent bg-violet-50 text-xs font-bold text-violet-700"
          >
            {ticket.changeRequest.crNumber}
          </Badge>
        ) : null}
      </div>

      <div className="overflow-hidden rounded-xl border border-amber-200 bg-[#fffff0] shadow-sm dark:border-amber-900/40 dark:bg-amber-950/20">
        <div className="border-b border-amber-200/80 px-4 py-4 dark:border-amber-900/30">
          <h1 className="text-xl font-bold leading-snug text-foreground break-words [overflow-wrap:anywhere]">
            {ticket.subject}
          </h1>
          <p className="mt-2 text-xs text-muted-foreground">
            {!isEmptyDisplayValue(display.createdByName) ? (
              <>
                Added by{" "}
                <span className="font-medium text-foreground">
                  {display.createdByName}
                </span>
                {createdAgo ? <> {createdAgo}</> : null}
              </>
            ) : createdAgo ? (
              <>Added {createdAgo}</>
            ) : null}
            {updatedAgo ? (
              <>
                {createdAgo || !isEmptyDisplayValue(display.createdByName)
                  ? ". "
                  : ""}
                Updated {updatedAgo}
              </>
            ) : null}
          </p>
        </div>

        {(leftAttributes.length > 0 || rightAttributes.length > 0) && (
          <div className="grid grid-cols-1 gap-4 px-4 py-4 sm:grid-cols-2 sm:gap-6">
            <div className="space-y-2.5 sm:space-y-2">
              {leftAttributes.map(row => (
                <AttributeRow key={row.label} label={row.label}>
                  {row.value}
                </AttributeRow>
              ))}
            </div>
            <div className="space-y-2.5 sm:space-y-2">
              {rightAttributes.map(row => (
                <AttributeRow key={row.label} label={row.label}>
                  {row.value}
                </AttributeRow>
              ))}
            </div>
          </div>
        )}
      </div>

      {description ? (
        <section className="mt-6 overflow-hidden rounded-xl border border-border bg-card shadow-sm">
          <div className="border-b border-border/80 bg-muted/30 px-4 py-2">
            <h3 className="text-sm font-semibold text-foreground">
              Description
            </h3>
          </div>
          <div className="px-4 py-4 text-foreground">
            <TicketDescriptionContent description={description} />
          </div>
        </section>
      ) : null}

      <TicketDetailFilesSection
        attachments={attachments}
        loading={attachmentsLoading}
        resolveUserLabel={resolveUserLabel}
      />

      {showAddNoteSection ? (
        <TicketDetailAddNoteSection ticketId={ticket.id} />
      ) : null}

      {ticket.tags.length > 0 ? (
        <section className="mt-6 flex flex-wrap gap-2">
          {ticket.tags.map(tag => (
            <Badge key={tag} variant="secondary" className="text-xs">
              {tag}
            </Badge>
          ))}
        </section>
      ) : null}

      <TicketDetailActivitySection
        activityEntries={activityEntries}
        activityPending={activityPending}
        activityError={activityError}
        notes={notes}
        notesPending={notesPending}
        notesError={notesError}
        resolveUserLabel={resolveUserLabel}
      />
    </div>
  );
}
